import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { forkJoin, from, of, timer } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { CoreActions } from './../actions/core.actions';
import { Language, Region, RegionProperty } from './../ridango.models';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import { Temporal } from '@js-temporal/polyfill';
import { PersonalCodeValidator } from '../validators/personal-code/personal-code.factory';
import { Device } from '@capacitor/device';
import Env from '../../env';

@Injectable({
    providedIn: 'root'
})
export class AppStartupService {
    private readonly timeoutMs = 5000;
    private region: Region = null;
    private selectedLanguage: Language = null;

    private transportationDayOffsetWarningShown = false;

    constructor(
        private authHttp: HttpClient,
        private notifications: NotificationService,
        private ngRedux: NgRedux<any>,
        private personalCodeValidator: PersonalCodeValidator,
        private actions: CoreActions,
        @Inject(LOCALE_ID) public locale: string,
        private ridangoAuth: AuthService,
        private translate: TranslateService,
        @Inject('ENV') private env: Env
    ) {}

    public getConfig(key: string) {
        return this.region[key];
    }

    public getPropertyByCode(propertyCode: string): RegionProperty {
        return this.region?.region_properties?.find((prop) => prop.code === propertyCode);
    }

    public doesPropertyExist(propertyCode: string): boolean {
        return this.region?.region_properties?.some((prop) => prop.code === propertyCode);
    }

    public getCurrentLanguage() {
        return this.selectedLanguage;
    }

    public getRegion() {
        return this.region;
    }

    public getTimezone(): string {
        return this.region?.timezone ? this.region?.timezone : 'Europe/Tallinn';
    }

    public getLocale(): string {
        return this.region?.settings?.LOCALE ? this.region?.settings?.LOCALE : 'et-ee';
    }

    get getTimezoneOffset(): string {
        return Temporal.Now.instant().toZonedDateTimeISO(this.getTimezone()).offset;
    }

    public getCurrency(): string {
        return this.region.currency ?? this.env?.CURRENCY ?? 'EUR';
    }
    public getCurrencySymbol(): string {
        const DEFAULT_CURRENCY = '€';
        const currencies = new Map<string, string>([
            ['EUR', '€'],
            ['SEK', 'SEK']
        ]);

        return currencies.get(this.getCurrency()) ?? DEFAULT_CURRENCY;
    }

    public isTransportationDayOffsetEnabled(): boolean {
        return !!(
            this.env.FORMATTING?.USE_TRANSPORTATION_DAY_OFFSET ?? this.env.PURCHASE?.USE_TRANSPORTATION_DAY_OFFSET
        );
    }

    public getTransportationDayOffset(_instant?: Temporal.Instant): Temporal.Duration {
        if (!this.isTransportationDayOffsetEnabled()) {
            return Temporal.Duration.from({ minutes: 0 });
        }
        if (typeof this.region?.transition_time_min !== 'number') {
            return Temporal.Duration.from({ minutes: 0 });
        }
        return Temporal.Duration.from({ minutes: this.region.transition_time_min });
    }

    /**
     * Cart / purchase preview end date for duration-based period tickets.
     *
     * `durationDays` is inclusive calendar days (day 1 = start date), matching
     * backend-sync `product_length` with `TICKET_END_TIME=0` (ends 23:59:59 on
     * the last day). Example: start 2026-07-16, length 30 → end 2026-08-14 23:59:59.
     *
     * Transportation-day display for *stored* ticket endings is handled by
     * `ridangoDateTime` (HYBRID), not here. LPAD-10280: do not add a full
     * `durationDays` and rely on region transition to subtract one day; pilet
     * region 14 has transition 0 and previously showed 31 calendar days.
     */
    public calculateTicketEndDate(date: Date | string, durationDays: number): Date {
        const startDate = date instanceof Date ? date : new Date(date);
        const startPlainDate = Temporal.PlainDate.from({
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            day: startDate.getDate()
        });
        const lastInclusiveDayOffset = Math.max(0, durationDays - 1);
        const finalEndDate = startPlainDate.add({ days: lastInclusiveDayOffset });
        return new Date(finalEndDate.year, finalEndDate.month - 1, finalEndDate.day, 23, 59, 59, 999);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public doCustomerQuery(env: any, channel = 'web'): Promise<any> {
        return new Promise((resolve) => {
            if (this.ridangoAuth.getLoginStatus()) {
                const url = `${env.API_BASE_URL}/${env.REGION_ID}/customer-account`;
                this.authHttp.get(url).subscribe(
                    () => {
                        resolve(true);
                    },
                    (e) => {
                        if (e.error === 'AUTH_REFRESH_ERR') {
                            this.ngRedux.dispatch({ type: 'CLEAR_SELECTED_PAYMENT_METHOD' });
                            this.ngRedux.dispatch({ type: 'CART_TERMS_AGREE_CHANGED', agreed: false });
                            this.ngRedux.dispatch({ type: 'RESET_TICKETS' });
                            this.ngRedux.dispatch({ type: 'RESET_SELECTED_MEDIUM' });
                            this.ngRedux.dispatch({ type: '[Ticket] Clear all local tickets' });
                            this.ngRedux.dispatch({ type: '[Related Cards]: Reset Related Cards' });
                            this.ngRedux.dispatch({ type: 'DELETE_SHOPPING_CART' });
                            this.ngRedux.dispatch({ type: '[ShoppingCart] Delete shopping cart' });

                            this.ridangoAuth.signOut();
                        }
                        resolve(true);
                    },
                    () => {
                        resolve(true);
                    }
                );
            } else {
                resolve(true);
            }
        });
    }

    public load(env: Env, channel = 'web'): Promise<any> {
        this.notifications.deleteAll();

        let urlApiRegion: string;
        let urlApiRegionLanguages: string;
        if (this.env.IS_NEW_AFC) {
            urlApiRegion = `${this.env.WMB.WMB_PUBLIC_API}/v1/region-settings`;
            urlApiRegionLanguages = `${this.env.WMB.WMB_PUBLIC_API}/v1/region-languages`;
        } else {
            urlApiRegion = `${this.env.API_BASE_URL}/regions/${this.env.REGION_ID}`;
            urlApiRegionLanguages = `${env.API_BASE_URL}/regions/${env.REGION_ID}/languages`;
        }

        const region = this.authHttp.get(urlApiRegion).pipe(
            timeout(this.timeoutMs),
            catchError(() => of({}))
        );
        const languages = this.authHttp.get(urlApiRegionLanguages).pipe(
            timeout(this.timeoutMs),
            catchError(() => of([]))
        );

        let termsUrl: string;
        if (this.env.ENABLE_TERMS_WMB_VERSION) {
            termsUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/terms-and-conditions/latest`;
        } else {
            termsUrl = `${env.API_BASE_URL}/${env.REGION_ID}/terms-and-conditions/channel/${channel}/version`;
        }
        const terms = this.authHttp.get(termsUrl).pipe(
            timeout(this.timeoutMs),
            catchError(() => of(null))
        );
        const deviceLanguage = from(Device.getLanguageCode()).pipe(map((language) => language.value));

        return new Promise((resolve, reject) => {
            forkJoin([region, languages, terms, deviceLanguage]).subscribe(
                (results) => {
                    const r = new Region();
                    this.region = Object.assign(r, results[0]);
                    this.region.languages = Object.assign([], results[1]);
                    this.region.terms = results[2];
                    const deviceLanguage = results[3];

                    this.locale = this.translate.currentLang;
                    if (this.locale) {
                        document.documentElement.lang = this.locale;
                    }

                    this.selectedLanguage = this.region.languages.find((l: Language) => {
                        return l.code === this.locale;
                    });

                    if (!this.selectedLanguage) {
                        this.selectedLanguage = this.region.languages.find((l: Language) => {
                            return l.code === deviceLanguage;
                        });
                    }

                    if (!this.selectedLanguage) {
                        this.selectedLanguage = this.region.languages.find((l: Language) => {
                            return l.default_language;
                        });
                    }

                    if (this.region.country_code) {
                        this.personalCodeValidator.initialize(this.region.country_code);
                    }
                    this.ngRedux.dispatch(this.actions.setRegion(this.region));
                    timer(1000).subscribe(() => {
                        this.ngRedux.dispatch(this.actions.setSelectedLanguage(this.selectedLanguage));
                    });
                },
                () => {
                    reject(true);
                },
                () => {
                    resolve(true);
                }
            );
        });
    }
}
